import {
  useEffect,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { IconeConfirmado, IconeMostrar, IconeOcultar } from './icones';
import { Seletor, type BuscaDoSeletor, type OpcaoDoSeletor } from './Seletor';

type PropsDoCampo = InputHTMLAttributes<HTMLInputElement> & {
  rotulo: string;
  dica?: string;
  /**
   * Campo de SEGREDO: mascarado, com o botão de mostrar e ocultar, e só de
   * escrita. Ver `CampoSecreto`.
   */
  segredo?: boolean;
};

export function Campo({ rotulo, dica, segredo = false, ...props }: PropsDoCampo): React.JSX.Element {
  if (segredo) return <CampoSecreto rotulo={rotulo} dica={dica} {...props} />;
  return (
    <label className="block space-y-1.5">
      <span className="text-sm text-zinc-400">{rotulo}</span>
      <input
        {...props}
        className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none transition focus:border-zinc-500"
      />
      {dica ? <span className="block text-xs text-zinc-500">{dica}</span> : null}
    </label>
  );
}

/**
 * A variante `segredo` do `Campo` (F17): as chaves dos provedores de modelos
 * (F2: NanoGPT e OpenRouter), e o que vier depois com o mesmo feitio.
 *
 * **Só de escrita, e isso é imposto aqui, não combinado com quem chama.** O
 * servidor nunca devolve o segredo — a tela só conhece o final dele —, e este
 * campo não aceita ser preenchido por fora: `defaultValue` é descartado e o
 * `type`/`autoComplete` de quem chama não passam. O único valor que ele mostra
 * é o que a pessoa acabou de digitar nesta visita.
 *
 * Mascarado por `type="password"`, com um botão para conferir o que foi
 * colado — chave de 70 caracteres colada com um espaço a mais só se descobre
 * olhando. Quando o valor volta a ficar vazio (salvou, ou apagou), o campo
 * volta a esconder: a próxima chave não pode nascer à vista porque a anterior
 * foi conferida.
 *
 * `autoComplete="off"`, sem `<form>` em volta e sem campo de usuário ao lado:
 * é isso que tira o gerenciador de senhas do navegador de cena — ele não
 * oferece preencher, e não pergunta se deve "salvar a senha" ao enviar.
 */
function CampoSecreto({
  rotulo,
  dica,
  ...props
}: Omit<PropsDoCampo, 'segredo'>): React.JSX.Element {
  const [visivel, setVisivel] = useState(false);
  const id = useId();
  const {
    defaultValue: _preenchimentoRecusado,
    type: _tipoIgnorado,
    autoComplete: _autoCompleteIgnorado,
    className: _classeIgnorada,
    id: idDeFora,
    ...resto
  } = props;
  const idDoCampo = idDeFora ?? id;
  const vazio = props.value === '' || props.value === undefined;

  useEffect(() => {
    if (vazio) setVisivel(false);
  }, [vazio]);

  return (
    <div className="space-y-1.5">
      <label htmlFor={idDoCampo} className="block text-sm text-zinc-400">
        {rotulo}
      </label>
      <div className="flex items-stretch gap-1.5">
        <input
          {...resto}
          id={idDoCampo}
          type={visivel ? 'text' : 'password'}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="min-w-0 flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 font-mono text-sm text-zinc-100 outline-none transition focus:border-zinc-500 pointer-coarse:text-base"
        />
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          aria-label={visivel ? 'Ocultar o que foi digitado' : 'Mostrar o que foi digitado'}
          title={visivel ? 'Ocultar o que foi digitado' : 'Mostrar o que foi digitado'}
          aria-pressed={visivel}
          aria-controls={idDoCampo}
          className="flex w-10 shrink-0 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-200 pointer-coarse:w-11"
        >
          {visivel ? <IconeOcultar /> : <IconeMostrar />}
        </button>
      </div>
      {dica ? <span className="block text-xs text-zinc-500">{dica}</span> : null}
    </div>
  );
}

/**
 * Escolher uma coisa numa lista, dentro de um formulário.
 *
 * É o `Seletor` do painel na variante `campo`, com o rótulo por fora amarrado
 * ao gatilho. Existe para o formulário não ter de repetir seis linhas de
 * `aria-labelledby`, `titulo` e cálculo do texto fechado a cada campo — e para
 * que trocar o desenho do seletor um dia seja mexer em dois arquivos.
 *
 * O que aparece fechado é sempre o rótulo da opção escolhida; sem escolha,
 * `vazio`. Um campo de escolha que não mostra a escolha é um campo quebrado.
 */
export function CampoDeEscolha<T extends string = string>({
  rotulo,
  rotuloOculto = false,
  dica,
  valor,
  opcoes,
  aoEscolher,
  vazio = 'escolha…',
  desabilitado = false,
  compacto = false,
  className,
  busca,
}: {
  rotulo: string;
  /** Barra estreita e linha de tabela não têm espaço para rótulo escrito —
   *  mas quem lê por leitor de tela continua precisando dele. */
  rotuloOculto?: boolean;
  dica?: string;
  valor: T | null;
  opcoes: OpcaoDoSeletor<T>[];
  aoEscolher: (id: T) => void;
  vazio?: string;
  desabilitado?: boolean;
  /** Pílula em vez de campo: para linha de tabela e barra lateral. */
  compacto?: boolean;
  className?: string;
  /** Busca no alto do menu, para lista grande (o catálogo do provedor de modelos). */
  busca?: BuscaDoSeletor;
}): React.JSX.Element {
  const idDoRotulo = useId();
  const escolhida = opcoes.find((o) => o.id === valor);

  return (
    // Sem rótulo à vista não há o que espaçar: o `space-y` daria ao controle um
    // topo de 6 px que o desalinharia do botão ao lado numa linha de formulário.
    <div className={(rotuloOculto ? '' : 'space-y-1.5 ') + (className ?? '')}>
      <span id={idDoRotulo} className={rotuloOculto ? 'sr-only' : 'block text-sm text-zinc-400'}>
        {rotulo}
      </span>
      <Seletor
        variante={compacto ? 'pilula' : 'campo'}
        idDoRotulo={idDoRotulo}
        titulo={rotulo}
        rotulo={escolhida?.rotulo ?? vazio}
        icone={escolhida?.icone}
        valor={valor}
        opcoes={opcoes}
        ocupado={desabilitado}
        /**
         * Num formulário `perigoso` é só a cor. Quem confirma o risco é a
         * caixa de marcar logo abaixo; perguntar duas vezes seguidas a mesma
         * coisa treina a pessoa a clicar em "Confirmar" sem ler.
         */
        confirmacao={() => null}
        aoEscolher={(id) => aoEscolher(id)}
        busca={busca}
      />
      {dica ? <span className="block text-xs text-zinc-500">{dica}</span> : null}
    </div>
  );
}

/**
 * Uma caixa de marcar desenhada por nós.
 *
 * O `<input type="checkbox">` nativo é pintado pelo sistema: no Windows, em
 * cima de uma tela escura, ele aparece como um quadrado branco de outro
 * programa — o mesmo defeito do `<select>`. Aqui o input continua existindo
 * (é ele que carrega o estado, o foco e o papel `checkbox`), só que escondido
 * com `sr-only`, e o quadrado ao lado é desenhado com `peer-checked`.
 *
 * O ✓ vive dentro do quadrado o tempo todo e vira `currentColor`: desmarcado
 * ele é transparente, marcado ele é escuro sobre âmbar. Um ícone só, sem par
 * de elementos para manter em sincronia.
 */
export function CaixaDeMarcar({
  marcada,
  aoMarcar,
  children,
  desabilitada = false,
  className,
}: {
  marcada: boolean;
  aoMarcar: (marcada: boolean) => void;
  children: ReactNode;
  desabilitada?: boolean;
  /** A moldura fica com quem chama: aviso âmbar, linha de lista, o que for. */
  className?: string;
}): React.JSX.Element {
  return (
    <label className={'flex cursor-pointer items-start gap-2.5 ' + (className ?? '')}>
      <input
        type="checkbox"
        checked={marcada}
        disabled={desabilitada}
        onChange={(e) => aoMarcar(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-zinc-600 bg-zinc-950/60 text-transparent transition peer-checked:border-amber-400 peer-checked:bg-amber-400 peer-checked:text-zinc-950 peer-focus-visible:ring-2 peer-focus-visible:ring-amber-300 peer-disabled:opacity-40"
      >
        <IconeConfirmado tamanho={12} />
      </span>
      <span className="min-w-0">{children}</span>
    </label>
  );
}

export function Botao({
  children,
  carregando,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  carregando?: boolean;
}): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'submit'}
      disabled={props.disabled ?? carregando}
      className="w-full rounded-md bg-zinc-100 px-3 py-2 font-medium text-zinc-900 transition hover:bg-white disabled:opacity-50"
    >
      {carregando ? 'Aguarde...' : children}
    </button>
  );
}

export function Aviso({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <p className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-sm text-red-300">
      {children}
    </p>
  );
}

export function Cartao({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-zinc-800 bg-zinc-900/60 p-6">
        <header className="space-y-1">
          <h1 className="text-lg font-semibold text-zinc-100">{titulo}</h1>
          {descricao ? <p className="text-sm text-zinc-400">{descricao}</p> : null}
        </header>
        {children}
      </div>
    </main>
  );
}
